import _ from 'lodash'

import { mapGetters } from 'vuex'

import { formatBytes } from '_/helpers/formatBytes'

import { store } from '_/vuex/store'

const photonConfig = require('~/config/config.json')

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    activeView: {
      required: true,
      type: String,
    },
    registeredModuleName: {
      type: String,
      required: true,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      viewSelectionMode: false,
    }
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      ui: 'ui/ui',
    }),

    /**
     * Define the assetsManager getter
     *
     * @return  {object}
     */
    assetsManager() {
      const getterName = `${this.registeredModuleName}/${this.registeredModuleName}`

      return this.$store.getters[getterName]
    },

    /**
     * Returns assets based on viewSelectionMode
     *
     * @return  {object}
     */
    assets() {
      if (this.viewSelectionMode) {
        return this.assetsManager.selectedAssets
      }

      return this.assetsManager.assets
    },

    /**
     * Date format getter
     *
     * @return  {string}
     */
    dateFormat() {
      return this.ui.photonConfig.dateFormat
    },

    /**
     * Returns the number of selected objects
     *
     * @return  {integer}
     */
    numberOfSelectedAssets() {
      return _.size(this.assetsManager.selectedAssets)
    },
  },

  /**
   * Set the methods
   *
   * @type  {object}
   */
  methods: {
    /**
     * return displaying title value
     *
     * @return  {string}
     */
    fileTitle(title, fileName) {
      let defaultLocale = this.ui.defaultLocale.languageCode
      return _.get(title, defaultLocale) || fileName
    },

    /**
     * return displaying title value
     *
     * @return  {string}
     */
    altText(altText) {
      let defaultLocale = this.ui.defaultLocale.languageCode
      return altText === null ? null : altText[defaultLocale]
    },

    /**
     * Dispatches the clearAllSelectedAssets action
     *
     * @return  {void}
     */
    clearAllSelectedAssets() {
      store.dispatch(`${this.assetsManager.moduleName}/clearAllSelectedAssets`)
    },

    /**
     * Checks if entry is published
     *
     * @param {Object} entry
     * @returns boolean
     */
    isPublished(entry) {
      const date_time = moment().format('YYYY-MM-DD HH:mm:ss')
      return moment(date_time).isSameOrAfter(
        _.get(entry, 'system.published_at')
      )
    },

    /**
     * Calculates if the 'active' class should be activated or not
     *
     * @return  {object}
     */
    isActiveAsset(id) {
      return (
        _.get(this.assetsManager.editedEntry, 'system.id') &&
        _.get(this.assetsManager.editedEntry, 'system.id') === id
      )
    },

    /**
     * Checks mime type to determine if a file is an image
     *
     * @param   {string}  mimeType
     * @return  {Boolean}
     */
    isImage(mimeType) {
      return photonConfig.allowedImageMimeTypes.indexOf(mimeType) > -1
    },

    /**
     * Calculates if the 'selected' class should be activated or not
     *
     * @return  {object}
     */
    isSelectedAsset(id) {
      return this.assetsManager.selectedAssetsIds.indexOf(id) >= 0
    },

    /**
     * Formats byte to human readable value
     *
     * @param   {integer}  bytes
     * @return  {string}
     */
    formatBytes,

    /**
     * Returns asset thumbnail
     *
     * @param   {object}  asset
     * @return  {string}
     */
    getAssetThumbnail(asset) {
      return _.has(asset, 'fields.file_url')
        ? asset.fields.file_url + '?w=120&h=90'
        : ''
    },

    /**
     * Emits the event upon file selection
     *
     * @param   {object}  asset
     * @return  {void}
     */
    selectAsset: _.throttle(
      function(asset) {
        this.$emit('selectedAsset', asset)
      },
      200,
      {
        leading: true,
        trailing: false,
      }
    ),

    /**
     * Emits the event upon file deselection
     *
     * @param   {object}  asset
     * @return  {void}
     */
    deselectAsset(asset) {
      this.$emit('deselectedAsset', asset)
    },

    /**
     * Toggles the view selection mode
     *
     * @return  {void}
     */
    toggleViewSelectionMode() {
      this.viewSelectionMode = !this.viewSelectionMode
    },

    /**
     * Updated at getter
     *
     * @param   {integer}  entry
     * @return  {string}
     */
    updatedAt(entry) {
      return entry.system.updated_at
        ? moment(entry.system.updated_at).format(this.dateFormat)
        : null
    },
  },

  watch: {
    numberOfSelectedAssets(newEntry, oldEntry) {
      if (newEntry <= 0 && this.viewSelectionMode) {
        this.toggleViewSelectionMode()
      }
    },
  },
}
